import { m } from "framer-motion";
import IconButton from "@mui/material/IconButton";
import Iconify from "src/components/iconify";
import { RouterLink } from "src/routes/components";
import { paths } from "src/routes/paths";
import {
  Divider,
  Link,
  MenuItem,
  Popover,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  IProfileSelectorProvider,
  PROVIDER_MODULES,
  ProviderModule,
  ProviderType,
} from "src/types/sportprovider";
import { t } from "i18next";
import CustomPopover, { usePopover } from "src/components/custom-popover";

// ----------------------------------------------------------------------
type Props = {
  provider: IProfileSelectorProvider;
};

export default function PublicProfileIcon({ provider }: Props) {
  const theme = useTheme();
  const popover = usePopover();

  const urltag = provider?.urltag;
  if (!urltag) return null;

  const profileUrl = paths.sportprovider.profile(urltag);
  const calendarUrl = paths.sportprovider.calendar(urltag);
  const trainingsUrl = paths.sportprovider.trainings(urltag);
  const venuesUrl = paths.sportprovider.venues(urltag);
  const passesUrl = paths.sportprovider.passes(urltag);
  const ticketsUrl = paths.sportprovider.tickets(urltag);
  const tournamentsUrl = paths.sportprovider.tournaments(urltag);
  const rankingsUrl = paths.sportprovider.rankings(urltag, 0);
  const newsUrl = paths.sportprovider.newsfeed(urltag);
  const liveStreamingUrl = paths.sportprovider.livestreaming(urltag);
  const rulesUrl = paths.sportprovider.rules(urltag);

  const ALL_ITEMS: Record<ProviderModule, { label: string; href: string }> = {
    profile: {
      label: t("profile"),
      href: profileUrl,
    },
    calendar: {
      label: t("calendar"),
      href: calendarUrl,
    },
    trainings: {
      label: t("trainings"),
      href: trainingsUrl,
    },
    venues: {
      label: t("venues"),
      href: venuesUrl,
    },
    passes: {
      label: t("passes"),
      href: passesUrl,
    },
    tickets: {
      label: t("tickets"),
      href: ticketsUrl,
    },
    tournaments: {
      label: t("tournaments"),
      href: tournamentsUrl,
    },
    rankings: {
      label: t("rankings"),
      href: rankingsUrl,
    },
    news: {
      label: t("news"),
      href: newsUrl,
    },
    rules: {
      label: t("rules"),
      href: rulesUrl,
    },
    livestreaming: {
      label: t("livestreaming"),
      href: liveStreamingUrl,
    },
  };

  const items = PROVIDER_MODULES[provider.type as ProviderType].map(
    (key) => ALL_ITEMS[key]
  );

  const openInNewIconSx = {
    color: theme.palette.info.main,
    width: 18,
    height: 15,
    ml: 0.5,
  };
  return (
    <>
      <Tooltip title={t("public_profile")}>
        <IconButton
          component={m.button}
          sx={{ width: 40, height: 40 }}
          onClick={popover.onOpen}
        >
          <Iconify
            icon="material-symbols:public"
            sx={{
              borderRadius: 0.65,
              width: 28,
              height: 35,
              mb: 0.5,
            }}
          />
        </IconButton>
      </Tooltip>

      <CustomPopover
        open={popover.open}
        onClose={popover.onClose}
        sx={{ width: 250, p: 0 }}
      >
        <Stack sx={{ p: 1 }}>
          {items.map((item) => (
            <MenuItem
              key={item.href}
              component={RouterLink}
              href={item.href}
              target="_blank"
              onClick={popover.onClose}
            >
              {item.label}
              <Iconify
                icon="cuida:open-in-new-tab-outline"
                sx={openInNewIconSx}
              />
            </MenuItem>
          ))}
        </Stack>
      </CustomPopover>
    </>
  );
}
